import React from 'react';
import Joi from 'joi-browser';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  withStyles,
  FormControl,
  CircularProgress,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  ListItemSecondaryAction,
  Typography,
  ListItem,
  ListItemText,
  Paper,
  Grid,
  List,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardHeader,
  CardContent,
  CardActions,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { TwitterPicker } from 'react-color';
import {
  mapValues,
  values,
  includes,
} from 'lodash';
import { updateTenant } from '@pingum/app-state-management/tenant/actions';
import { showLogsModal } from '@pingum/app-state-management/log/actions';
import { TenantService } from '@pingum/api-services';

import AuditLogsButton from '../../../widgets/AuditLogsButton';
import AvatarEditor from '../../../widgets/AvatarEditor';
import Tenant from '../../../widgets/Tenant';
import TenantAvatar from '../../../widgets/Tenant/TenantAvatar';
import colors from '../../../../utils/colors';

import './styles.scss';

const styles = () => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  textField: {
    width: 200,
    maxWidth: '100%',
    marginBottom: '1em',
  },
  longTextField: {
    width: 400,
    maxWidth: '100%',
  },
  actionButton: { marginLeft: 'auto' },
  floatRight: { float: 'right' },
  removeAvatarButton: {
    float: 'right',
    margin: 8,
  },
});

export const getTenantUrl = (name) => (
  name !== undefined && name.length > 0
    ? TenantService.getTenantUrl(name)
    : ''
);

const getInitialState = (tenant) => ({
  displayColorPicker: false,
  updateTenantRequest: {
    name: tenant.name,
    avatar: tenant.avatar,
    icon: null,
    displayName: tenant.displayName,
    iconColor: tenant.iconColor,
    allowRequestsToJoin: tenant.allowRequestsToJoin,
    contactEmail: tenant.contactEmail,
  },
  validation: {
    hasChanged: false,
    contactEmail: {
      isValid: true,
      message: '',
    },
    displayName: {
      isValid: true,
      message: '',
    },
  },
});

export class SettingsCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.avatarEditorRef = React.createRef();

    this.state = {
      ...getInitialState(props.tenant),
      isAdvancedVisible: false,
    };
  }

  getAdvancedProps = () => {
    const {
      createdByName,
      createdDate,
      updatedByName,
      updatedDate,
    } = this.props.tenant;

    return [{
      value: createdByName,
      label: 'Created by: ',
    }, {
      value: new Date(createdDate).toLocaleDateString(),
      label: 'Date created: ',
    }, {
      value: updatedDate
        ? updatedByName
        : 'n/a',
      label: 'Last updated by: ',
    }, {
      value: updatedDate
        ? new Date(updatedDate).toLocaleDateString()
        : 'n/a',
      label: 'Date last updated: ',
    }];
  }

  updateTenantData = () => {
    if (!(includes(values(mapValues(this.state.validation, (validation) => {
      if (validation instanceof Object) {
        return validation.isValid;
      }
      return validation;
    })), false))) {
      this.props.updateTenant(this.state.updateTenantRequest);
      this.setState((prevState) => ({
        validation: {
          ...prevState.validation,
          hasChanged: false,
        },
      }));
    }
  }

  handleAvatarChange = (icon) => {
    this.setState((prevState) => ({
      updateTenantRequest: {
        ...prevState.updateTenantRequest,
        icon,
        avatar: icon,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
      },
    }));
    if (!icon) {
      this.avatarEditorRef.current.onCancel();
    }
  }

  handleDisplayNameChange = (e) => {
    const changedValue = e.target.value;
    const isValid
      = changedValue !== null
        && changedValue !== undefined
        && changedValue.length >= 2
        && changedValue.length <= 100;
    this.setState((prevState) => ({
      updateTenantRequest: {
        ...prevState.updateTenantRequest,
        displayName: changedValue,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
        displayName: {
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid display name (between 2 and 100 characters in length).</span>
            : '',
        },
      },
    }));
  }

  handleIconColorChange = (e) => {
    const changedValue = e.hex;
    this.setState((prevState) => ({
      updateTenantRequest: {
        ...prevState.updateTenantRequest,
        iconColor: changedValue,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
      },
    }));
    this.handleColorPickerClose();
  }

  handleAllowRequestsToJoinChange = () => {
    this.setState((prevState) => ({
      updateTenantRequest: {
        ...prevState.updateTenantRequest,
        allowRequestsToJoin:
          !prevState.updateTenantRequest.allowRequestsToJoin,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
      },
    }));
  }

  handleContactEmailChange = (e) => {
    const contactEmail = e.target.value;
    const isValid = Joi.validate(contactEmail, Joi.string().email()).error === null;
    this.setState((prevState) => ({
      updateTenantRequest: {
        ...prevState.updateTenantRequest,
        contactEmail,
      },
      validation: {
        ...prevState.validation,
        hasChanged: true,
        contactEmail: {
          isValid,
          message: !isValid
            ? <span><br />Please provide a valid email address.</span>
            : '',
        },
      },
    }));
  }

  handleColorPickerClose = () => {
    this.setState({ displayColorPicker: false });
  }

  handleExampleTenantClick = () => {
    this.setState({ displayColorPicker: true });
  }

  resetState = () => {
    this.setState({
      ...getInitialState(this.props.tenant),
      isAdvancedVisible: false,
    });
    this.avatarEditorRef.current.onCancel();
  }

  showActionButtons = () => (this.state.validation.hasChanged || this.props.updateTenantError)
      && this.state.validation.contactEmail.isValid && this.state.validation.displayName.isValid

  showLogsModal = () => {
    this.props.showLogsModal({
      tenantName: this.props.tenant.name,
      service: 'tenant',
      actions: ['Update Tenant', 'Create New Tenant'],
    });
  }

  toggleAdvancedVisibility = () => {
    this.setState((prevState) => ({
      ...prevState,
      isAdvancedVisible: !prevState.isAdvancedVisible,
    }));
  }

  render() {
    return (
      <Card>
        <CardHeader
          title="Organization Settings"
          subheader="General information for your organization"
          action={
            <AuditLogsButton onClick={this.showLogsModal} />
          }
        />
        <CardContent>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={5}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    Direct Link
                  </Typography>
                  <Typography variant="caption" gutterBottom>
                    Share Pingum with those in your organization
                  </Typography>
                  <div>
                    <a href={getTenantUrl(this.props.tenant.name)} target="_blank" rel="noopener noreferrer">
                      {getTenantUrl(this.props.tenant.name)}
                    </a>
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    Preview
                  </Typography>
                  <Typography variant="caption" gutterBottom>
                    How your organization appears to others
                  </Typography>
                  <Tenant tenant={this.state.updateTenantRequest} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={7} xl={6}>
              <List>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      General Information
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      error={!this.state.validation.displayName.isValid}
                      className={this.props.classes.textField}
                      variant="outlined"
                      fullWidth
                    >
                      <InputLabel htmlFor="tenant-display-name">Display Name</InputLabel>
                      <OutlinedInput
                        id="tenant-display-name"
                        value={this.state.updateTenantRequest.displayName}
                        onChange={this.handleDisplayNameChange}
                        disabled={this.props.isFetching}
                        labelWidth={110}
                        size="small"
                      />
                      <FormHelperText id="display-name-helper-text">
                        {
                          !this.state.validation.displayName.isValid
                          && this.state.validation.displayName.message
                        }
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={!this.state.validation.contactEmail.isValid}
                      aria-describedby="contact-email"
                      className={this.props.classes.longTextField}
                      variant="outlined"
                    >
                      <InputLabel htmlFor="contact-email">
                        Organization Contact Email
                      </InputLabel>
                      <OutlinedInput
                        id="contact-email"
                        value={this.state.updateTenantRequest.contactEmail}
                        onChange={this.handleContactEmailChange}
                        disabled={this.props.isFetching}
                        labelWidth={200}
                      />
                      <FormHelperText id="user-email-helper-text">
                        {
                          !this.state.validation.contactEmail.isValid
                          && this.state.validation.contactEmail.message
                        }
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  {/* <Grid item xs={12} lg={9} xl={7}>
                    <ListItem disableGutters>
                      <ListItemText
                        primary="Allow users to request to join your organization"
                        style={{
                            paddingRight: '2rem',
                          }}
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          id="allow-requests-to-join-switch"
                          checked={this.state.updateTenantRequest.allowRequestsToJoin}
                          onChange={this.handleAllowRequestsToJoinChange}
                          value="allowRequests"
                          color="primary"
                          disabled={this.props.isFetching}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Grid> */}
                  <Grid item xs={12} lg={9} xl={7}>
                    <ListItem disableGutters>
                      <ListItemText
                        primary="Default Color"
                        secondary="Used when no logo is found"
                      />
                      <ListItemSecondaryAction>
                        <div
                          className="circle"
                          style={{ backgroundColor: this.state.updateTenantRequest.iconColor }}
                          role="presentation"
                          onClick={this.handleExampleTenantClick}
                        />
                        {
                            this.state.displayColorPicker
                            && (
                              <div
                                style={{
                                  position: 'absolute',
                                  right: 50,
                                  top: -75,
                                }}
                              >
                                <div
                                  style={{
                                    position: 'fixed',
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                  }}
                                  onClick={this.handleColorPickerClose}
                                  onKeyDown={this.handleColorPickerClose}
                                  role="presentation"
                                />
                                <Paper>
                                  <TwitterPicker
                                    color={this.state.updateTenantRequest.iconColor}
                                    colors={this.props.colors}
                                    onChange={this.handleIconColorChange}
                                    direction="vertical"
                                  />
                                </Paper>
                              </div>
                            )
                          }
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Grid>
                  <Grid item xs={12} lg={9} xl={7}>
                    <ListItem disableGutters>
                      <ListItemText
                        primary="Logo"
                      />
                      {
                        this.state.updateTenantRequest.avatar
                        && (
                          <Tooltip title="Remove uploaded profile image">
                            <IconButton
                              className={this.props.classes.removeAvatarButton}
                              aria-label="Remove Upload"
                              onClick={() => { this.handleAvatarChange(''); }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )
                      }
                      <ListItemSecondaryAction>
                        <TenantAvatar tenant={this.state.updateTenantRequest} />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <AvatarEditor
                      ref={this.avatarEditorRef}
                      onChange={this.handleAvatarChange}
                    />
                  </Grid>
                </Grid>
              </List>
            </Grid>
          </Grid>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={5}>
              <Typography variant="subtitle1">
                Advanced
              </Typography>
            </Grid>
            <Grid item xs={12} sm={7}>
              <Grid item xs={12} lg={9} xl={6}>
                <Button
                  color="primary"
                  size="small"
                  onClick={this.toggleAdvancedVisibility}
                >
                  {
                    this.state.isAdvancedVisible
                      ? 'Less'
                      : 'More'
                  }
                </Button>
              </Grid>
              {
                this.state.isAdvancedVisible
                && (
                  <div>
                    {
                    this.getAdvancedProps().map((prop) => (
                      <Grid item xs={12} lg={9} xl={6} style={{ marginBottom: 6 }} key={prop.label}>
                        <Typography variant="caption">
                          {prop.label}
                        </Typography>
                        {prop.value}
                      </Grid>
                    ))
                  }
                  </div>
                )
              }
            </Grid>
          </Grid>
          <CardActions className={this.props.classes.floatRight}>
            {
              (this.props.isFetching || this.state.validation.hasChanged
                || this.props.updateTenantError)
                  && (
                    <Button
                      className={this.props.classes.actionButton}
                      onClick={this.resetState}
                      disabled={this.props.isFetching}
                    >
                      Cancel
                    </Button>
                  )
            }
            {
              (this.props.isFetching || this.showActionButtons())
              && (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={this.updateTenantData}
                  disabled={this.props.isFetching}
                >
                  Save
                  {
                  this.props.isFetching
                    && <CircularProgress size={20} style={{ position: 'absolute' }} color="inherit" />
                }
                </Button>
              )
            }
          </CardActions>
        </CardContent>
      </Card>
    );
  }
}

SettingsCardComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  tenant: PropTypes.object,
  updateTenant: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  updateTenantError: PropTypes.bool.isRequired,
  colors: PropTypes.arrayOf(PropTypes.string),
  showLogsModal: PropTypes.func.isRequired,
};

SettingsCardComponent.defaultProps = {
  colors,
  tenant: {
    displayName: '',
    contactEmail: '',
  },
};

export const mapStateToProps = (state) => ({
  isFetching: state.tenant.isFetchingUpdateTenant,
  tenant: state.tenant.tenant,
  updateTenantError: state.tenant.updateTenantError,
});

export const mapDispatchToProps = (dispatch) => ({
  updateTenant: (request) => dispatch(updateTenant(request)),
  showLogsModal: (request) => dispatch(showLogsModal(request)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(SettingsCardComponent));
